/**
 * useGrowthData — pulls trailing 14 months of daily net-sales per outlet
 * and aggregates into monthly buckets. Two parallel queries (7 + 7 months)
 * to stay under the anon-role 3s statement_timeout on outlet_sales_v.
 *
 * Output shape lets the page render brand / city / outlet at both MoM and
 * YoY perspectives without re-querying.
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

interface DailyRow {
  outlet_id: string;
  date: string;
  net_sales: number | null;
  discount_total: number | null;
}

export interface OutletMonth {
  outletId: string;
  /** YYYY-MM */
  month: string;
  netSales: number;
  discountTotal: number;
}

export interface GrowthState {
  /** Sorted list of YYYY-MM strings, oldest → newest. ~25 entries. */
  months: string[];
  /** Per-outlet monthly totals (sparse if outlet was inactive). */
  byOutletMonth: OutletMonth[];
  /** Pan-Flax meta ads spend per month (sum across all campaigns). */
  metaAdsByMonth: Map<string, number>;
  isLoading: boolean;
}

function isoMonth(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function useGrowthData(): GrowthState {
  // Trailing 25 months ending with current month — enough to overlay 12
  // months this year against the same 12 months last year, plus the current
  // partial month for the latest MoM delta.
  const today = new Date();
  const newest = endOfMonth(today);
  const months: string[] = [];
  for (let i = 24; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    months.push(isoMonth(d));
  }

  // Split into FIVE ~5-month windows. PostgREST's db_max_rows is now 25000
  // (migration) so we'd survive a single fetch — but chunking keeps each
  // request under the 3s anon statement_timeout and lets them run in
  // parallel for faster wall time.
  const ranges: Array<{ from: Date; to: Date }> = [];
  for (let i = 0; i < 5; i++) {
    // i=0 oldest, i=4 newest
    const startOffset = 24 - i * 5;
    const endOffset = startOffset - 4;
    const from = startOfMonth(new Date(today.getFullYear(), today.getMonth() - startOffset, 1));
    const to =
      i === 4
        ? newest
        : endOfMonth(new Date(today.getFullYear(), today.getMonth() - endOffset, 1));
    ranges.push({ from, to });
  }

  const salesQueries = useQueries({
    queries: ranges.map((r) => ({
      queryKey: ['growth-daily', isoDate(r.from), isoDate(r.to)],
      queryFn: async (): Promise<DailyRow[]> => {
        const { data, error } = await marketing
          .from('outlet_sales_v')
          .select('outlet_id, date, net_sales, discount_total')
          .gte('date', isoDate(r.from))
          .lte('date', isoDate(r.to))
          .range(0, 4999);
        if (error) throw error;
        return (data ?? []) as DailyRow[];
      },
      staleTime: 5 * 60_000,
    })),
  });

  // Meta ads — pan-Flax monthly spend. Cheaper to fetch over the full window
  // in one shot since meta_ad_daily is much smaller than outlet_sales_v.
  // If the table is empty (Supermetrics secret not wired yet) we just get
  // an empty array — every chart degrades gracefully to "no ads data".
  const adsFrom = ranges[0]?.from ?? today;
  const adsTo = ranges[ranges.length - 1]?.to ?? today;
  const adsQ = useQueries({
    queries: [
      {
        queryKey: ['growth-meta-ads', isoDate(adsFrom), isoDate(adsTo)],
        queryFn: async (): Promise<Array<{ date: string; spend: number | null }>> => {
          const { data, error } = await marketing
            .from('meta_ad_daily')
            .select('date, spend')
            .gte('date', isoDate(adsFrom))
            .lte('date', isoDate(adsTo))
            .range(0, 4999);
          if (error) throw error;
          return (data ?? []) as Array<{ date: string; spend: number | null }>;
        },
        staleTime: 5 * 60_000,
      },
    ],
  });

  const isLoading =
    salesQueries.some((q) => q.isLoading) || adsQ.some((q) => q.isLoading);

  const byOutletMonth = useMemo<OutletMonth[]>(() => {
    // "outletId|YYYY-MM" → { net, discount }
    const bucket = new Map<string, { net: number; discount: number }>();
    for (const q of salesQueries) {
      for (const row of q.data ?? []) {
        if (!row.outlet_id || !row.date) continue;
        const key = `${row.outlet_id}|${row.date.slice(0, 7)}`;
        const b = bucket.get(key) ?? { net: 0, discount: 0 };
        b.net += Number(row.net_sales ?? 0);
        b.discount += Number(row.discount_total ?? 0);
        bucket.set(key, b);
      }
    }
    const out: OutletMonth[] = [];
    for (const [k, v] of bucket) {
      const parts = k.split('|');
      const outletId = parts[0] ?? '';
      const month = parts[1] ?? '';
      if (outletId && month) {
        out.push({ outletId, month, netSales: v.net, discountTotal: v.discount });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesQueries.map((q) => q.dataUpdatedAt).join('|')]);

  const metaAdsByMonth = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    for (const q of adsQ) {
      for (const row of q.data ?? []) {
        if (!row.date) continue;
        const m = row.date.slice(0, 7);
        map.set(m, (map.get(m) ?? 0) + Number(row.spend ?? 0));
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adsQ.map((q) => q.dataUpdatedAt).join('|')]);

  return { months, byOutletMonth, metaAdsByMonth, isLoading };
}
